<template>
    <Dialog :visible="visible" @update:visible="$emit('update:visible', $event)" modal header="Transaction History"
        class="w-[95svw] sm:w-[70vw] h-[85svh] sm:h-[70vh]" :maximizable="true" :closeOnEscape="false">
        <AddTransaction v-if="isAdmin" :walletId="walletId" :currentBalance="localBalance"
            @transaction-added="handleTransactionAdded" />
        <DataTable :value="transactions" :loading="transactionsLoading"  
        class="p-datatable-sm flex-grow" scrollable>
            <Column field="date" header="Date"  :style="headerStyles" >
                <template #body="slotProps">
                    {{ formatDateTime(slotProps.data.createdAt) }}
                </template>
            </Column>
            <Column field="type" header="Type" :style="headerStyles">
                <template #body="slotProps">
                    {{ slotProps.data.type ? capitalizeFirstLetter(slotProps.data.type) : '-' }}
                </template>
            </Column>
            <Column field="description" header="Description"  :style="headerStyles" />
            <Column field="amount" header="Amount" :style="headerStyles">
                <template #body="slotProps">
                    {{ formatCurrencyWithCents(slotProps.data.amount) }}
                </template>
            </Column>
            <Column field="balance" header="Balance" :style="headerStyles">
                <template #body="slotProps">
                    {{ formatCurrencyWithCents(slotProps.data.balance) }}
                </template>
            </Column>
            <Column field="status" header="Status" :style="headerStyles">
                <template #body="slotProps">
                    <Tag :severity="getStatusSeverity(slotProps.data.status)">
                        {{ slotProps.data.status ? capitalizeFirstLetter(slotProps.data.status) : '-' }}
                    </Tag>
                </template>
            </Column>
        </DataTable>
    </Dialog>
</template>

<script setup>
import { ref, onMounted, defineProps, defineEmits, watch, computed } from 'vue';
import { formatDateTime } from '@/utils/dateHelper';
import { capitalizeFirstLetter } from '@/utils/string';
import AddTransaction from '@/components/finance/AddTransaction.vue';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import { formatCurrencyWithCents } from '@/utils/currencies';
import WalletService from '@/services/WalletService';
import { useStore } from 'vuex';
import { headerStyles } from '@/global/headerStyles';

const store = useStore();
const isAdmin = computed(() => store.getters.isAdmin);

const props = defineProps({
    visible: {
        type: Boolean,
        required: true
    },
    walletId: {
        type: String,
        required: true
    },
    currentBalance: {
        type: Number,
        required: true
    }
});

const emit = defineEmits(['update:visible', 'wallet-updated']);
const transactions = ref([]);
const transactionsLoading = ref(false);

// Local ref for balance
const localBalance = ref(props.currentBalance);

// Watch for prop changes to update local ref
watch(() => props.currentBalance, (newBalance) => {
    localBalance.value = newBalance;
});

// Initialize on mount
onMounted(() => {
    localBalance.value = props.currentBalance;
});

const loadTransactions = async () => {
    transactionsLoading.value = true;
    try {
        const response = await WalletService.getWalletTransactions(props.walletId);
        transactions.value = response.data;
        console.log('transactions', transactions.value);
    } catch (error) {
        console.error('Failed to load transactions:', error);
    } finally {
        transactionsLoading.value = false;
    }
};

const getStatusSeverity = (status) => {
    const severities = {
        completed: 'success',
        pending: 'warning',
        failed: 'danger'
    };
    return severities[status] || 'info';
};

watch(() => props.visible, (newValue) => {
    if (newValue) {
        loadTransactions();
    }
}, { immediate: true });

const handleTransactionAdded = async (transaction) => {
    await loadTransactions();
    emit('wallet-updated');
};
</script>